// The issue mutations' optimistic steps over the site's cached issue list —
// one copy, so update, quick status and comment can't drift apart.

import type { QueryClient } from '@tanstack/react-query';
import type { IIssue } from '../../../trusstor/index.js';
import { issuesKeys } from '../queryKeys.js';

export type IssuesCache = { issues: IIssue[] } | undefined;

/** Rewrite one cached issue; a no-op while the site's list isn't cached. */
export function patchCachedIssue(qc: QueryClient, siteId: string | null, issueId: string, update: (issue: IIssue) => IIssue): void {
  qc.setQueryData<IssuesCache>(issuesKeys.list(siteId), (current) =>
    current && { ...current, issues: current.issues.map((i) => (i._id === issueId ? update(i) : i)) },
  );
}

/** Start an optimistic edit: stop in-flight reads, keep the list as it was, apply the edit. */
export async function beginIssuePatch(
  qc: QueryClient,
  siteId: string | null,
  issueId: string,
  update: (issue: IIssue) => IIssue,
): Promise<IssuesCache> {
  await qc.cancelQueries({ queryKey: issuesKeys.list(siteId) });
  const previous = qc.getQueryData<IssuesCache>(issuesKeys.list(siteId));
  patchCachedIssue(qc, siteId, issueId, update);
  return previous;
}

/**
 * Undo a failed edit: put back ONLY this issue's earlier copy, and only if it
 * is still cached. Restoring the whole snapshot would clobber other concurrent
 * edits and resurrect an issue a concurrent delete removed mid-flight.
 */
export function rollbackIssue(qc: QueryClient, siteId: string | null, previous: IssuesCache, issueId: string): void {
  const before = previous?.issues.find((i) => i._id === issueId);
  if (before) patchCachedIssue(qc, siteId, issueId, () => before);
}
